import React, { useEffect, useMemo, useState } from "react";
import { Spinner } from "../../../components";
import {
  useAdminUsageComparison,
  useAdminUsageTimeseries,
} from "../../../api/hooks/admin";
import { adjustWorkflowTotal, isoFromDateInput, MetricsMode } from "../shared/constants";
import {
  USAGE_CATEGORIES,
  UsageCategory,
  UsageGranularity,
} from "../../../api/admin/types";
import KpiCards from "../shared/KpiCards";
import CsvDownloadButton from "../shared/CsvDownloadButton";
import AgencyPicker from "./AgencyPicker";
import UserTable, { UserTableRow } from "./UserTable";
import UserHeatmap from "./UserHeatmap";
import TrendChart from "../AgencyTotals/TrendChart";

const _zeroTotals = (): Record<UsageCategory, number> =>
  USAGE_CATEGORIES.reduce(
    (acc, c) => ({ ...acc, [c]: 0 }),
    {} as Record<UsageCategory, number>
  );

interface UserTotalsProps {
  mode: MetricsMode;
  granularity: UsageGranularity;
  startA: string;
  endA: string;
  startB: string;
  endB: string;
  includeTestAccounts: boolean;
  showEpic: boolean;
  onFetchingChange?: (fetching: boolean) => void;
}

const UserTotals: React.FC<UserTotalsProps> = ({
  mode,
  granularity,
  startA,
  endA,
  startB,
  endB,
  includeTestAccounts,
  showEpic,
  onFetchingChange,
}) => {
  const [agencyId, setAgencyId] = useState<string | null>(null);
  const [agencyName, setAgencyName] = useState<string | null>(null);
  const [teamFilter, setTeamFilter] = useState<string | null>(null);
  const [drillUserId, setDrillUserId] = useState<string | null>(null);

  const tsParams =
    agencyId && startA && endA
      ? {
          startDate: isoFromDateInput(startA),
          endDate: isoFromDateInput(endA),
          granularity,
          agencyId,
          groupBy: "user" as const,
          includeTestAccounts,
        }
      : null;
  const tsQuery = useAdminUsageTimeseries(tsParams);

  const cmpParams =
    mode === "compare" && agencyId && startA && endA && startB && endB
      ? {
          periodAStart: isoFromDateInput(startA),
          periodAEnd: isoFromDateInput(endA),
          periodBStart: isoFromDateInput(startB),
          periodBEnd: isoFromDateInput(endB),
          agencyId,
          groupBy: "user" as const,
          includeTestAccounts,
        }
      : null;
  const cmpQuery = useAdminUsageComparison(cmpParams);

  const isFetching =
    tsQuery.isFetching || (mode === "compare" && cmpQuery.isFetching);
  useEffect(() => {
    onFetchingChange?.(isFetching);
  }, [isFetching, onFetchingChange]);

  const teams = useMemo(() => {
    if (!tsQuery.data) return [] as { id: string; name: string }[];
    const byId = new Map<string, string>();
    for (const u of tsQuery.data.groups) {
      if (u.team_id && !byId.has(u.team_id)) {
        byId.set(u.team_id, u.team_name || u.team_id);
      }
    }
    return Array.from(byId.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [tsQuery.data]);

  const drillUserBuckets = drillUserId && tsQuery.data?.by_group?.[drillUserId];
  const drillUserMeta = drillUserId
    ? tsQuery.data?.groups.find((g) => g.id === drillUserId) ?? null
    : null;

  // Build the table rows from whichever query best reflects "current state".
  // - In compare mode: use the comparison endpoint's groups (already includes
  //   all agency seats incl. dormant ones, after the BE seat fix) and show
  //   period B totals so users with activity ONLY in B (Nimbity case) appear.
  // - In single mode: keep summing the timeseries buckets for the chosen window.
  const tableRows: UserTableRow[] = useMemo(() => {
    if (mode === "compare") {
      const groups = cmpQuery.data?.groups ?? [];
      return groups.map((g) => {
        const totals = { ..._zeroTotals(), ...g.period_b.totals };
        return {
          user: g,
          totals,
          total: adjustWorkflowTotal(g.period_b.total, totals, showEpic),
        };
      });
    }
    const users = tsQuery.data?.groups ?? [];
    const byUser = tsQuery.data?.by_group ?? {};
    return users.map((u) => {
      const buckets = byUser[u.id] || [];
      const totals = _zeroTotals();
      let total = 0;
      for (const b of buckets) {
        for (const c of USAGE_CATEGORIES) {
          totals[c] += b.totals[c] ?? 0;
        }
        total += b.total;
      }
      return { user: u, totals, total: adjustWorkflowTotal(total, totals, showEpic) };
    });
  }, [mode, tsQuery.data, cmpQuery.data, showEpic]);

  const tableCaption =
    mode === "compare"
      ? "Showing period B totals per user. See heatmap above for period-over-period change."
      : undefined;

  // In compare mode the user is looking at both periods, so the "no activity"
  // hint must consider both. (Previously we only summed period A timeseries
  // buckets, which made agencies with activity in B look empty.)
  const totalActivity =
    mode === "compare"
      ? (cmpQuery.data?.period_a.total ?? 0) + (cmpQuery.data?.period_b.total ?? 0)
      : (tsQuery.data?.buckets.reduce((s, b) => s + b.total, 0) ?? 0);
  const showEmptyHint = !!agencyId && totalActivity === 0;

  // The reused /admin-retrieve-user-export endpoint returns every user (no
  // agency filter), so we still allow a download but only once an agency has
  // been picked, since that's when the user is operating on a meaningful slice.
  const csvStart = mode === "compare" ? (startA < startB ? startA : startB) : startA;
  const csvEnd = mode === "compare" ? (endA > endB ? endA : endB) : endA;
  const csvStartIso = csvStart ? isoFromDateInput(csvStart) : "";
  const csvEndIso = csvEnd ? isoFromDateInput(csvEnd) : "";
  const csvReady = !!agencyId && !!csvStartIso && !!csvEndIso;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center", width: "100%" }}>
      <div
        style={{
          width: "100%",
          maxWidth: 1200,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <CsvDownloadButton
          mode="users"
          startDate={csvStartIso}
          endDate={csvEndIso}
          filenamePrefix="users_export"
          disabled={!csvReady}
          disabledReason={
            !agencyId ? "Pick an agency first" : "Pick a date range first"
          }
        />
      </div>
      <AgencyPicker
        selectedAgencyId={agencyId}
        onSelect={(id, name) => {
          setAgencyId(id);
          setAgencyName(name);
          setTeamFilter(null);
          setDrillUserId(null);
        }}
      />

      {!agencyId ? (
        <div style={{ color: "#6b7280", padding: 24 }}>Pick an agency to drill down.</div>
      ) : tsQuery.isLoading ||
        tsQuery.isPreviousData ||
        (mode === "compare" && (cmpQuery.isLoading || cmpQuery.isPreviousData)) ? (
        <div style={{ padding: 40 }}>
          <Spinner />
        </div>
      ) : (
        <>
          {showEmptyHint ? (
            <div
              style={{
                background: "#fff7ed",
                border: "1px solid #fed7aa",
                borderRadius: 8,
                padding: 14,
                width: "100%",
                maxWidth: 1200,
                color: "#7c2d12",
                fontSize: 14,
              }}
            >
              No activity for {agencyName ?? "this agency"} in the selected range.{" "}
              {includeTestAccounts
                ? "Try widening the date range."
                : <>Try widening the range, or check <b>Include test accounts</b> in the toolbar.</>}
            </div>
          ) : null}
          {teams.length > 0 ? (
            <div
              style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: 12,
                width: "100%",
                maxWidth: 1200,
                boxSizing: "border-box",
                display: "flex",
                gap: 8,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginRight: 8, textTransform: "uppercase", letterSpacing: 0.4 }}>
                Team:
              </div>
              {([null, ...teams] as Array<null | { id: string; name: string }>).map((t) => {
                const teamId = t === null ? null : t.id;
                const active = teamId === teamFilter;
                return (
                  <button
                    key={teamId ?? "__all__"}
                    onClick={() => setTeamFilter(teamId)}
                    style={{
                      padding: "6px 12px",
                      borderRadius: 6,
                      border: active ? "1px solid #3949ab" : "1px solid #e5e7eb",
                      background: active ? "#eef2ff" : "#fff",
                      color: active ? "#3949ab" : "#374151",
                      cursor: "pointer",
                      fontSize: 13,
                      fontWeight: active ? 600 : 500,
                    }}
                  >
                    {t === null ? "All teams" : t.name}
                  </button>
                );
              })}
            </div>
          ) : null}

          {mode === "compare" && cmpQuery.data ? (
            <KpiCards
              totalA={cmpQuery.data.period_a.total}
              totalB={cmpQuery.data.period_b.total}
              pctChange={cmpQuery.data.delta.pct_change}
              labelA={`${agencyName ?? "Agency"} — period A`}
              labelB={`${agencyName ?? "Agency"} — period B`}
            />
          ) : null}

          {mode === "compare" && cmpQuery.data ? (
            <UserHeatmap
              groups={cmpQuery.data.groups.filter(
                (g) => !teamFilter || g.team_id === teamFilter
              )}
              agencyName={agencyName ?? undefined}
              periodALabel={`${cmpQuery.data.period_a.start?.slice(0, 10)} – ${cmpQuery.data.period_a.end?.slice(0, 10)}`}
              periodBLabel={`${cmpQuery.data.period_b.start?.slice(0, 10)} – ${cmpQuery.data.period_b.end?.slice(0, 10)}`}
              onUserClick={(uid) => setDrillUserId(uid)}
              showEpic={showEpic}
            />
          ) : null}

          {tableRows.length > 0 || tsQuery.data ? (
            <UserTable
              rows={tableRows}
              filterTeamId={teamFilter}
              onUserClick={(uid) => setDrillUserId(uid)}
              caption={tableCaption}
              showEpic={showEpic}
            />
          ) : null}

          {drillUserId && drillUserBuckets ? (
            <TrendChart
              buckets={drillUserBuckets}
              title={`${drillUserMeta?.label ?? drillUserId} — usage trend`}
              subtitle={`${tsQuery.data?.start_date.slice(0, 10)} → ${tsQuery.data?.end_date.slice(0, 10)}, by ${granularity}`}
              showEpic={showEpic}
            />
          ) : null}
        </>
      )}
    </div>
  );
};

export default UserTotals;
